import React, { useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { useChatContext } from '../context/ChatContext';

interface ChatContainerProps {
  welcomeMessage?: string;
  assistantName?: string;
  assistantAvatar?: string;
  suggestedPrompts?: string[];
  onClose?: () => void;
  showCloseButton?: boolean;
  containerClassName?: string;
  primaryColor?: string;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  welcomeMessage = "We're here to help you manage your debt. Any information you share will be used to assist you on your financial journey.",
  assistantName = 'John',
  assistantAvatar,
  suggestedPrompts = [],
  onClose,
  showCloseButton = true,
  containerClassName = '',
  primaryColor = '#3b82f6'
}) => {
  const { messages, isLoading, sendMessage } = useChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Handler for ChatInput to adapt the simple string message to the SendMessageOptions format
  const handleSendMessage = (message: string) => {
    sendMessage({ message });
  };
  
  return (
    <div 
      className={`skit-ai-llm-flex skit-ai-llm-h-full skit-ai-llm-w-full skit-ai-llm-flex-col skit-ai-llm-overflow-hidden skit-ai-llm-rounded-lg ${containerClassName} skit-ai-llm-border skit-ai-llm-border-gray-200`}
      // style={{ 
      //   boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 2px 10px rgba(0, 0, 0, 0.1)'
      // }}
    >
      {/* Header - Styled similar to the screenshot */}
      <div 
        className="skit-ai-llm-text-white skit-ai-llm-p-4 skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-justify-between"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-space-x-2">
          <div className="skit-ai-llm-w-10 skit-ai-llm-h-10 skit-ai-llm-rounded-full skit-ai-llm-overflow-hidden skit-ai-llm-bg-white">
            {assistantAvatar ? (
              <img 
                src={assistantAvatar} 
                alt={assistantName} 
                className="skit-ai-llm-w-full skit-ai-llm-h-full skit-ai-llm-object-cover"
              />
            ) : (
              <div 
                className="skit-ai-llm-w-full skit-ai-llm-h-full skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-justify-center skit-ai-llm-font-bold"
                style={{ color: primaryColor }}
              >
                {assistantName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <div className="skit-ai-llm-font-bold">Chat with</div>
            <div>{assistantName}</div>
          </div>
        </div>
        
        {showCloseButton && onClose && (
          <button
            onClick={onClose}
            className="skit-ai-llm-text-white hover:skit-ai-llm-text-gray-200 skit-ai-llm-transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>
      
      {/* Welcome message - like in the screenshot */}
      {welcomeMessage && (
        <div className="skit-ai-llm-px-4 skit-ai-llm-py-3 skit-ai-llm-text-center skit-ai-llm-text-gray-600 skit-ai-llm-bg-gray-50 skit-ai-llm-border-b skit-ai-llm-text-xs skit-ai-llm-leading-4">
          {welcomeMessage}
        </div>
      )}
      
      {/* Messages container */}
      <div className="skit-ai-llm-flex-1 skit-ai-llm-p-4 skit-ai-llm-overflow-y-auto skit-ai-llm-bg-white">
        {/* Message list */}
        <div className="skit-ai-llm-space-y-4">
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              assistantAvatar={assistantAvatar}
              assistantName={assistantName}
              primaryColor={primaryColor}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Input area */}
      <ChatInput
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        suggestedPrompts={suggestedPrompts}
        primaryColor={primaryColor}
      />
      
      {/* Footer with Skit.ai logo */}
      <div className="text-right skit-ai-llm-text-xs skit-ai-llm-text-gray-400 skit-ai-llm-py-5 skit-ai-llm-px-3 skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-justify-end skit-ai-llm-border-t skit-ai-llm-bg-white">
        <span className="skit-ai-llm-mr-2">POWERED BY</span>
        <div className="skit-ai-llm-flex skit-ai-llm-items-center">
          <img 
            src="https://skit.ai/wp-content/uploads/2024/11/skit-icon.svg" 
            alt="Skit.ai" 
            className="skit-ai-llm-h-4 skit-ai-llm-mr-1" 
          />
          <span className="skit-ai-llm-font-bold">skit.ai</span>
        </div>
      </div>
    </div>
  );
};

export default ChatContainer; 